import React, { useState, useEffect, useRef } from "react";
import {
    IonPage,
    IonContent,
    IonHeader,
    IonLoading,
} from "@ionic/react";
import {
    Box,
    Container,
    Paper,
    Typography,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Avatar,
    Divider,
    IconButton,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Alert,
} from "@mui/material";
import {
    Download,
    VerifiedUser,
    School,
    AccountCircle,
    ArrowBack,
    CalendarMonth,
    VpnKey,
} from "@mui/icons-material";
import { useLocation, useHistory, useParams } from "react-router-dom";
import { styled, ThemeProvider, createTheme } from "@mui/material/styles";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { QRCodeCanvas } from "qrcode.react";
import { baseUrl, config } from "../common/utils";
import axios from "axios";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

// Import Logo Asset
import DeltaLogo from "../asset/Delta_logo.png";

// ============================================
// Elite Compact Theme - Matching DeltaGovPortal
// ============================================
const reportTheme = createTheme({
    palette: {
        primary: {
            main: "#243b5a",
        },
        secondary: {
            main: "#f0c45a",
        },
        background: {
            default: "#f8fafc",
            paper: "#ffffff",
        },
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "Arial", sans-serif',
        h5: {
            fontWeight: 700,
            color: "#1e293b",
        },
        h6: {
            fontWeight: 800,
            color: "#0f172a",
        },
        body2: {
            color: "#1e293b",
            fontWeight: 600,
            fontSize: "0.85rem",
        },
    },
});

// ============================================
// Styled Components
// ============================================
const MainContainer = styled(Container)(({ theme }) => ({
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(6),
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(1),
    },
}));

const ReportPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    borderRadius: "0px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
    border: "1.5px solid #000",
    position: "relative",
    overflow: "hidden",
    background: "#ffffff",
    maxWidth: "800px",
    margin: "0 auto",
    [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(2),
    },
}));

const ProfileBox = styled(Box)(({ theme }) => ({
    border: "1px solid #000",
    padding: theme.spacing(1.5),
    backgroundColor: "#ffffff",
    marginBottom: theme.spacing(2),
}));

const DateLabel = styled(Typography)({
    fontSize: "9px",
    fontWeight: 700,
    textTransform: "uppercase",
    color: "#334155",
    marginBottom: "5px",
    display: "flex",
    justifyContent: "flex-end"
});

const Watermark = styled(Box)({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%) rotate(-45deg)",
    opacity: 0.015,
    fontSize: "10rem",
    fontWeight: 900,
    color: "#243b5a",
    pointerEvents: "none",
    whiteSpace: "nowrap",
    zIndex: 0,
});

const ResultDisplay: React.FC = () => {
    const location = useLocation<any>();
    const history = useHistory();
    const { studentNumber } = useParams<{ studentNumber: string }>();

    const [resultData, setResultData] = useState<any>(location.state?.result || null);
    const [viewVersion, setViewVersion] = useState<"basic" | "full">("basic");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const logoRef = useRef<HTMLImageElement>(null);
    const passportRef = useRef<HTMLImageElement>(null);
    const qrRef = useRef<HTMLCanvasElement>(null);

    // Helper function to get image data for PDF (matching StudentList.tsx logic)
    const getImageDataForPDF = async (imagePath: string): Promise<string | null> => {
        if (!imagePath) return null;

        // Check if it's already a base64 string
        if (imagePath.startsWith("data:")) return imagePath;

        // Construct full URL
        const url = `${baseUrl()}assets${imagePath}`;

        try {
            const response = await fetch(url);
            if (response.ok) {
                const blob = await response.blob();
                return new Promise<string>((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result as string);
                    reader.readAsDataURL(blob);
                });
            }
        } catch (error) {
            console.error("Fetch fallback error:", error);
        }
        return null;
    };

    const { accessPin, studentNumber: stateStudentNumber } = location.state || {};
    const activeStudentNumber = stateStudentNumber || studentNumber;

    useEffect(() => {
        if (!resultData && activeStudentNumber) {
            fetchResult();
        }
    }, [activeStudentNumber, accessPin]);

    const fetchResult = async () => {
        setLoading(true);
        try {
            const response = await axios.post(
                baseUrl() + "results/checkResult",
                {
                    accessPin: accessPin || "",
                    studentNumber: activeStudentNumber
                },
                config
            );
            if (response.data.status === true) {
                setResultData(response.data.data);
            } else {
                setError(response.data.message || "Result not found.");
            }
        } catch (err: any) {
            setError(err.response?.data?.message || "An error occurred during verification.");
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <IonLoading isOpen={true} message="Verifying Certificate..." />;

    if (error || !resultData) {
        return (
            <IonPage>
                <IonContent>
                    <Box sx={{ p: 4, textAlign: "center", height: "100vh", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                        <Alert severity="error" sx={{ mb: 2 }}>{error || "Record state lost."}</Alert>
                        <Button variant="outlined" onClick={() => history.push("/login_check_result")}>Return to Portal</Button>
                    </Box>
                </IonContent>
            </IonPage>
        );
    }

    const isBlocked = String(resultData.blocked) === "1" || String(resultData.studentInfo?.blocked) === "1";
    if (isBlocked) {
        return (
            <IonPage>
                <IonContent>
                    <Box sx={{ p: 4, textAlign: "center", height: "100vh", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                        <Alert severity="warning" sx={{ mb: 2 }}>Result has been blocked. Contact ministry.</Alert>
                        <Button variant="outlined" onClick={() => history.push("/login_check_result")}>Return to Portal</Button>
                    </Box>
                </IonContent>
            </IonPage>
        );
    }

    const resData = resultData.resultData || {};
    const grades = [resData.engGrd, resData.aritGrd, resData.gpGrd, resData.rgsGrd];
    const isAllEmpty = grades.every(g => g === null || g === undefined || String(g).trim() === "" || String(g).trim() === "-");

    if (isAllEmpty) {
        return (
            <IonPage>
                <IonContent>
                    <Box sx={{ p: 4, textAlign: "center", height: "100vh", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                        <Alert severity="info" sx={{ mb: 2 }}>Result not uploaded.</Alert>
                        <Button variant="outlined" onClick={() => history.push("/login_check_result")}>Return to Portal</Button>
                    </Box>
                </IonContent>
            </IonPage>
        );
    }

    const getLgaNameByCode = (codeOrStudentNum: string): string => {
        if (!codeOrStudentNum) return "N/A";
        const cleanStr = String(codeOrStudentNum).trim();
        const prefix = cleanStr.substring(0, 2);
        const id = parseInt(prefix, 10);

        const localGovernments = [
            { id: 1, name: "Aniocha North" },
            { id: 2, name: "Aniocha South" },
            { id: 3, name: "Bomadi" },
            { id: 4, name: "Burutu" },
            { id: 5, name: "Ethiope East" },
            { id: 6, name: "Ethiope West" },
            { id: 7, name: "Ika North East" },
            { id: 8, name: "Ika South" },
            { id: 9, name: "Isoko North" },
            { id: 10, name: "Isoko South" },
            { id: 11, name: "Ndokwa East" },
            { id: 12, name: "Ndokwa West" },
            { id: 13, name: "Okpe" },
            { id: 14, name: "Oshimili North" },
            { id: 15, name: "Oshimili South" },
            { id: 16, name: "Patani" },
            { id: 17, name: "Sapele" },
            { id: 18, name: "Udu" },
            { id: 19, name: "Ughelli North" },
            { id: 20, name: "Ughelli South" },
            { id: 21, name: "Ukwuani" },
            { id: 22, name: "Uvwie" },
            { id: 23, name: "Warri North" },
            { id: 24, name: "Warri South" },
            { id: 25, name: "Warri South West" },
        ];

        const found = localGovernments.find(lga => lga.id === id);
        return found ? found.name : cleanStr;
    };

    const rawStudentInfo = resultData.studentInfo || {};

    // Build studentInfo defensively to support both nested and flat responses
    const studentInfo = {
        studentNumber: rawStudentInfo.studentNumber || resultData.EXAMINATIONNO || resultData.examinationNo || "N/A",
        firstname: rawStudentInfo.firstname || resultData.FNAME || resultData.fName || "",
        lastname: rawStudentInfo.lastname || resultData.LNAME || resultData.lName || "",
        othername: rawStudentInfo.othername || resultData.MNAME || resultData.mName || "",
        gender: rawStudentInfo.gender || resultData.SEXCD || resultData.sexCd || "N/A",
        dateOfBirth: rawStudentInfo.dateOfBirth || resultData.DOB || resultData.dob || null,
        passport: rawStudentInfo.passport || resultData.PASSPORT || resultData.passport || "",
        passportBase64: rawStudentInfo.passportBase64 || "",
        schoolName: rawStudentInfo.schoolName || resultData.SCHOOLNAME || resultData.schoolName || resultData.SCHOOLCODE || resultData.institutionCd || "N/A",
        lgaName: getLgaNameByCode(resultData.lgaCd || rawStudentInfo.studentNumber || resultData.EXAMINATIONNO || resultData.examinationNo || "17"),
        year: rawStudentInfo.year || resultData.SESSIONYR || resultData.sessionYr || "N/A",
        remark: resultData.remark || rawStudentInfo.remark || resultData.REMARK || resultData.remark || "N/A"
    };

    const religiousType = String(resultData.scores?.religious?.type || "");
    const religiousSubjectName = religiousType === "2"
        ? "ISLAMIC RELIGIOUS STUDIES"
        : (religiousType === "1" ? "CHRISTIAN RELIGIOUS STUDIES" : "RELIGIOUS STUDIES");

    // Build scores from resultData.resultData (holds final examination grades)
    const allScores = [
        { subject: "ENGLISH STUDIES", grade: resultData.resultData?.engGrd || "-" },
        { subject: "MATHEMATICS", grade: resultData.resultData?.aritGrd || "-" },
        { subject: "GENERAL PAPER", grade: resultData.resultData?.gpGrd || "-" },
        { subject: religiousSubjectName, grade: resultData.resultData?.rgsGrd || "-" }
    ];

    const displayScores = allScores.filter((s: any) => {
        const emptyGrade = s.grade === null || s.grade === undefined || String(s.grade).trim() === "" || String(s.grade).trim() === "-";
        return !emptyGrade;
    });

    const accCode = resultData.accCode || "N/A";
    const isFull = viewVersion === "full";
    const currentPrintDate = dayjs().format("DD-MM-YYYY HH:mm");

    const formatDOB = (dob: any) => {
        if (!dob) return "N/A";
        const strDob = String(dob).trim();
        const parsed = dayjs(strDob, ["DD/MM/YYYY", "YYYY-MM-DD", "DD-MM-YYYY", "MM/DD/YYYY"]);
        return parsed.isValid() ? parsed.format("DD-MM-YYYY") : strDob;
    };

    const handleDownload = async (includeDOB: boolean = true) => {
        // Use a slightly shorter custom height (240mm instead of 297mm A4)
        // to remove the huge blank space at the bottom of the certificate
        const doc = new jsPDF({ format: [210, 240] });
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;

        // Draw outer page border
        doc.setLineWidth(0.6);
        doc.rect(8, 8, pageWidth - 16, pageHeight - 16);

        // --- Header (Compact) ---
        doc.setFont("helvetica", "bold");
        doc.setFontSize(13);
        doc.text("FEDERAL REPUBLIC OF NIGERIA", pageWidth / 2, 17, { align: "center" });
        doc.text("FEDERAL REPUBLIC OF NIGERIA", (pageWidth / 2) + 0.15, 17, { align: "center" });

        if (logoRef.current) {
            try {
                const canvas = document.createElement("canvas");
                const size = Math.min(logoRef.current.naturalWidth, logoRef.current.naturalHeight);
                canvas.width = size;
                canvas.height = size;
                const ctx = canvas.getContext("2d");
                if (ctx) {
                    ctx.beginPath();
                    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
                    ctx.clip();
                    const sx = (logoRef.current.naturalWidth - size) / 2;
                    const sy = (logoRef.current.naturalHeight - size) / 2;
                    ctx.drawImage(logoRef.current, sx, sy, size, size, 0, 0, size, size);
                    doc.addImage(canvas.toDataURL("image/png"), 'PNG', (pageWidth / 2) - 10, 21, 20, 20);
                }
            } catch (e) {
                console.warn("Logo failed to load into PDF", e);
            }
        }

        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text("MINISTRY OF PRIMARY EDUCATION, ASABA", pageWidth / 2, 49, { align: "center" });
        doc.text("MINISTRY OF PRIMARY EDUCATION, ASABA", (pageWidth / 2) + 0.15, 49, { align: "center" });
        doc.text("DELTA STATE", pageWidth / 2, 55, { align: "center" });
        doc.text("DELTA STATE", (pageWidth / 2) + 0.15, 55, { align: "center" });

        doc.setFontSize(14);
        doc.text("COGNITIVE/PLACEMENT CERTIFICATION RESULT", pageWidth / 2, 63, { align: "center" });
        doc.text("COGNITIVE/PLACEMENT CERTIFICATION RESULT", (pageWidth / 2) + 0.15, 63, { align: "center" });
        doc.setFontSize(11);
        doc.text(`SESSION: ${studentInfo.year}`, pageWidth / 2, 69, { align: "center" });

        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        doc.text(`DATE PRINTED: ${currentPrintDate}`, 190, 72, { align: "right" });

        // --- Arranged Particulars (Single Column Layout) ---
        doc.setFontSize(10.5);
        let pY = 82;
        const labelX = 25;
        const valueX = 65;

        const addDetailLine = (label: string, value: string, boldVal: boolean = false) => {
            doc.setFont("helvetica", "bold");
            doc.text(`${label}:`, labelX, pY);
            doc.text(`${label}:`, labelX + 0.15, pY);
            doc.setFont("helvetica", boldVal ? "bold" : "normal");

            const lines = doc.splitTextToSize(value || "N/A", 120);
            doc.text(lines, valueX, pY);
            doc.text(lines, valueX + 0.15, pY);
            pY += (lines.length * 6); // Dynamic shift based on lines
        };

        addDetailLine("LGA", studentInfo.lgaName);
        addDetailLine("SCHOOL", studentInfo.schoolName);
        addDetailLine("CANDIDATE NAME", `${studentInfo.lastname.toUpperCase()} ${studentInfo.firstname.toUpperCase()} ${studentInfo.othername || ""}`);
        addDetailLine("EXAMINATION NO", studentInfo.studentNumber);

        if (includeDOB) {
            addDetailLine("DATE OF BIRTH", formatDOB(studentInfo.dateOfBirth));
        }
        addDetailLine("SEX", studentInfo.gender.toUpperCase());
        addDetailLine("ACCESS PIN", accCode);

        if (isFull && studentInfo.passport) {
            let added = false;
            const isPng = studentInfo.passport.toUpperCase().endsWith(".PNG");
            const format = isPng ? "PNG" : "JPEG";

            // Attempt 1: Safe DOM extraction (No jsPDF corruption if tainted)
            if (passportRef.current && passportRef.current.complete && passportRef.current.naturalWidth > 0) {
                try {
                    const canvas = document.createElement("canvas");
                    canvas.width = passportRef.current.naturalWidth;
                    canvas.height = passportRef.current.naturalHeight;
                    const ctx = canvas.getContext("2d");
                    if (ctx) {
                        ctx.drawImage(passportRef.current, 0, 0);
                        // If tainted, this line throws safely BEFORE touching jsPDF
                        const dataUrl = canvas.toDataURL(`image/${format.toLowerCase()}`);
                        doc.addImage(dataUrl, format, 165, 81, 20, 24);
                        added = true;
                    }
                } catch (e) {
                    console.warn("DOM canvas extraction tainted/failed", e);
                }
            }

            // Attempt 2: Fetch and Base64 fallbacks
            if (!added) {
                try {
                    let portraitData = await getImageDataForPDF(studentInfo.passport);
                    if (!portraitData && studentInfo.passportBase64) {
                        portraitData = studentInfo.passportBase64;
                    }
                    if (portraitData) {
                        const fmt = portraitData.includes("image/png") || isPng ? "PNG" : "JPEG";
                        doc.addImage(portraitData, fmt, 165, 81, 20, 24);
                        added = true;
                    }
                } catch (e) {
                    console.warn("Fallback image fetch failed", e);
                }
            }

            // Attempt 3: Safe Public CORS Proxy
            if (!added && baseUrl().includes("http")) {
                try {
                    const url = `${baseUrl()}assets${studentInfo.passport}`;
                    // corsproxy.io safely forwards exact bytes
                    const proxyUrl = `https://corsproxy.io/?url=${encodeURIComponent(url)}`;
                    const response = await fetch(proxyUrl);
                    if (response.ok) {
                        const blob = await response.blob();
                        // CRITICAL: Only proceed if the proxy returned an actual image! Prevents jsPDF crash.
                        if (blob.type.includes("image")) {
                            const dataUrl = await new Promise<string>((resolve) => {
                                const reader = new FileReader();
                                reader.onloadend = () => resolve(reader.result as string);
                                reader.readAsDataURL(blob);
                            });
                            const fmt = dataUrl.includes("image/png") || isPng ? "PNG" : "JPEG";
                            doc.addImage(dataUrl, fmt, 165, 81, 20, 24);
                            added = true;
                        }
                    }
                } catch (e) {
                    console.warn("Safe CORS proxy failed", e);
                }
            }
        }

        // Border around particulars (calculated dynamically)
        doc.setLineWidth(0.3);
        doc.rect(20, 76, 170, pY - 73);

        // --- Results Table ---
        autoTable(doc, {
            startY: pY + 5,
            head: [["SUBJECT", "GRADE"]],
            body: displayScores.map((s: any) => [s.subject.toUpperCase(), s.grade]),
            headStyles: {
                fillColor: [245, 245, 245],
                textColor: [0, 0, 0],
                fontSize: 12,
                fontStyle: 'bold',
                lineWidth: 0.2,
                lineColor: [0, 0, 0]
            },
            columnStyles: {
                0: { cellWidth: 145, halign: 'left' },
                1: { cellWidth: 25, halign: 'center' }
            },
            styles: { fontSize: 10.5, fontStyle: 'bold', cellPadding: 3, textColor: [0, 0, 0], font: "helvetica", lineWidth: 0.2, lineColor: [0, 0, 0] },
            alternateRowStyles: { fillColor: [255, 255, 255] },
            margin: { left: 20, right: 20 },
            theme: 'grid'
        });

        const finalY = (doc as any).lastAutoTable.finalY + 10;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text(`REMARK: ${studentInfo.remark.toUpperCase()}`, 20, finalY);
        doc.text(`REMARK: ${studentInfo.remark.toUpperCase()}`, 20.15, finalY);

        // Seal (QR Code)
        if (qrRef.current) {
            try {
                const qrData = qrRef.current.toDataURL("image/png");
                doc.addImage(qrData, 'PNG', 165, finalY - 5, 25, 25);
            } catch (e) {
                doc.rect(165, finalY - 5, 25, 25);
            }
        } else {
            doc.rect(165, finalY - 5, 25, 25);
        }

        doc.setFontSize(5);
        doc.text("VERIFIED", 177.5, finalY + 22, { align: "center" });

        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("DELTA STATE MINISTRY OF PRIMARY EDUCATION", pageWidth / 2, pageHeight - 15, { align: "center" });
        doc.text("DELTA STATE MINISTRY OF PRIMARY EDUCATION", (pageWidth / 2) + 0.15, pageHeight - 15, { align: "center" });

        doc.save(`Delta_Result_${studentInfo.studentNumber}.pdf`);
    };

    return (
        <ThemeProvider theme={reportTheme}>
            <IonPage>
                <IonHeader className="ion-no-border">
                    <HeaderBox>
                        <IconButton onClick={() => history.push("/login_check_result")} sx={{ color: "white", mr: 1 }}>
                            <ArrowBack />
                        </IconButton>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#cbd5e1" }}>
                            Electronic Result Verification
                        </Typography>
                    </HeaderBox>
                </IonHeader>

                <IonContent>
                    <MainContainer maxWidth="md">
                        <Stack direction="row" spacing={1} sx={{ mb: 2, justifyContent: "space-between" }}>
                            <ToggleButtonGroup
                                value={viewVersion}
                                exclusive
                                onChange={(_, v) => v && setViewVersion(v)}
                                size="small"
                            >
                                {/* <ToggleButton value="full" sx={{ textTransform: "none", fontWeight: 700, px: 2 }}>Print with Passport</ToggleButton> */}
                                <ToggleButton value="basic" sx={{ textTransform: "none", fontWeight: 700, px: 2 }}>Print without Passport</ToggleButton>
                            </ToggleButtonGroup>

                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="contained"
                                    startIcon={<Download />}
                                    onClick={() => handleDownload(true)}
                                    sx={{ bgcolor: "primary.main", fontWeight: 800, borderRadius: "4px" }}
                                >
                                    Print with DOB
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<Download />}
                                    onClick={() => handleDownload(false)}
                                    sx={{ fontWeight: 800, borderRadius: "4px" }}
                                >
                                    Print without DOB
                                </Button>
                            </Stack>
                        </Stack>

                        <ReportPaper elevation={0}>
                            <Watermark>OFFICIAL</Watermark>

                            {/* Header Group */}
                            <Box sx={{ textAlign: "center", mb: 2 }}>
                                <Typography variant="h6" sx={{ letterSpacing: "0.5px", fontSize: "1.15rem", fontWeight: 900 }}>
                                    FEDERAL REPUBLIC OF NIGERIA
                                </Typography>
                                <Box sx={{ py: 1 }}>
                                    <img
                                        ref={logoRef}
                                        src={DeltaLogo}
                                        alt="Delta Logo"
                                        style={{ width: "70px", height: "auto" }}
                                    />
                                </Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 900, fontSize: "0.95rem" }}>
                                    MINISTRY OF PRIMARY EDUCATION, ASABA
                                </Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1, fontSize: "0.95rem" }}>
                                    DELTA STATE
                                </Typography>
                                <Divider sx={{ borderBottomWidth: 2, borderColor: "#000", mb: 2 }} />
                                <Typography variant="h6" sx={{ mb: 0.5, letterSpacing: "1px" }}>
                                    COGNITIVE/PLACEMENT CERTIFICATION RESULT
                                </Typography>
                                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                                    {studentInfo.year} SESSION
                                </Typography>
                            </Box>

                            <DateLabel>Date Printed: {currentPrintDate}</DateLabel>

                            {/* Profile Box */}
                            <ProfileBox>
                                <Grid container spacing={1}>
                                    <Grid item xs={isFull && studentInfo.passport ? 9 : 12}>
                                        <Grid container columnSpacing={0}>
                                            <Grid item xs={12}><Detail label="LGA" val={studentInfo.lgaName} /></Grid>
                                            <Grid item xs={12}><Detail label="School" val={studentInfo.schoolName} /></Grid>
                                            <Grid item xs={12} sx={{ my: 0.5 }}><Detail label="Candidate Name" val={`${studentInfo.lastname} ${studentInfo.firstname} ${studentInfo.othername || ""}`} /></Grid>
                                            <Grid item xs={12}><Detail label="Examination No" val={studentInfo.studentNumber} /></Grid>
                                            <Grid item xs={12}><Detail label="Date of Birth" val={formatDOB(studentInfo.dateOfBirth)} /></Grid>
                                            <Grid item xs={12}><Detail label="Sex" val={studentInfo.gender} /></Grid>
                                            <Grid item xs={12}><Detail label="Access Pin" val={accCode} highlight /></Grid>
                                        </Grid>
                                    </Grid>
                                    {isFull && studentInfo.passport && (
                                        <Grid item xs={3} sx={{ display: "flex", justifyContent: "flex-end" }}>
                                            <img
                                                ref={passportRef}
                                                src={`${baseUrl()}assets${studentInfo.passport}`}
                                                alt="Student"
                                                style={{ width: "90px", height: "105px", border: "1px solid #000", padding: "2px" }}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            </ProfileBox>

                            {/* Data Table */}
                            <TableContainer sx={{ border: "1px solid #000" }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: "#eee" }}>
                                            <TableCell sx={{ fontWeight: 900, color: "#000", borderRight: "1px solid #000" }}>SUBJECT</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 900, color: "#000", width: 80 }}>GRADE</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {displayScores.map((row: any, i: number) => (
                                            <TableRow key={i}>
                                                <TableCell sx={{ fontWeight: 700, borderRight: "1px solid #000", py: 0.8 }}>{row.subject.toUpperCase()}</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 900 }}>{row.grade}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Lower Section */}
                            <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                                <Box>
                                    <Typography variant="overline" sx={{ fontWeight: 900, display: "block", lineHeight: 1 }}>REMARK:</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 900, color: "primary.main" }}>{studentInfo.remark.toUpperCase()}</Typography>
                                </Box>

                                <Box sx={{ textAlign: "center" }}>
                                    <Box sx={{ p: 0.5, border: "1px solid #000" }}>
                                        <QRCodeCanvas
                                            ref={qrRef}
                                            value={`https://deltamobse.ng/verify/${studentInfo.studentNumber}`}
                                            size={65}
                                            level="H"
                                        />
                                    </Box>
                                    <Typography variant="caption" display="block" sx={{ fontWeight: 900, mt: 0.5, fontSize: "7px" }}>OFFICIAL SEAL</Typography>
                                </Box>
                            </Box>

                            <Box sx={{ mt: 4, textAlign: "center", borderTop: "1px solid #ddd", pt: 1, pb: 1 }}>
                                <Typography variant="caption" display="block" sx={{ fontWeight: 900, fontSize: "11px" }}>
                                    DELTA STATE MINISTRY OF PRIMARY EDUCATION
                                </Typography>
                            </Box>
                        </ReportPaper>
                    </MainContainer>
                </IonContent>
            </IonPage>
        </ThemeProvider>
    );
};

const Detail = ({ label, val, highlight }: { label: string; val: string; highlight?: boolean }) => (
    <Box sx={{ display: "flex", mb: 0.4, gap: 1 }}>
        <Typography sx={{ fontWeight: 800, fontSize: "0.75rem", minWidth: "95px", color: "#475569" }}>{label}:</Typography>
        <Typography sx={{ fontWeight: 800, fontSize: "0.75rem", color: highlight ? "#243b5a" : "#000" }}>
            {(val || "N/A").toUpperCase()}
        </Typography>
    </Box>
);

const HeaderBox = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    height: "50px",
    padding: "0 20px",
    display: "flex",
    alignItems: "center",
    width: "100%",
}));

export default ResultDisplay;
